import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '@/lib/apiAuth';
import { supabaseAdmin } from '@/lib/supabaseAdmin';
import {
  classifyDocument,
  type ClassificationResult,
} from '@/lib/documents/classifier';

interface ClassifyResponse {
  success: boolean;
  data?: ClassificationResult;
  error?: string;
}

/**
 * POST /api/documents/classify
 *
 * Body (option A – existing document):
 *   { document_id: string }
 *
 * Body (option B – raw fields):
 *   { filename: string, mime_type?: string, text_content?: string }
 */
export default withAuth(async (req, res) => {
  if (req.method !== 'POST') {
    return res.status(405).json({ success: false, error: 'Method not allowed' });
  }

  try {
    const { document_id, filename, mime_type, text_content } = req.body;

    let result: ClassificationResult;

    if (document_id) {
      // Option A: classify from existing document record
      const { data: doc, error: docError } = await supabaseAdmin
        .from('documentos_financieros')
        .select('nombre_archivo, tipo_documento')
        .eq('id', document_id)
        .single();

      if (docError || !doc) {
        return res.status(404).json({
          success: false,
          error: `Documento no encontrado: ${docError?.message ?? document_id}`,
        } as ClassifyResponse);
      }

      result = classifyDocument(doc.nombre_archivo, undefined, undefined);
    } else if (filename) {
      // Option B: classify from provided metadata
      result = classifyDocument(filename, mime_type, text_content);
    } else {
      return res.status(400).json({
        success: false,
        error: 'Se requiere document_id o filename para clasificar.',
      } as ClassifyResponse);
    }

    return res.status(200).json({ success: true, data: result } as ClassifyResponse);
  } catch (error: any) {
    console.error('[classify] Error:', error);
    return res.status(500).json({
      success: false,
      error: error.message || 'Error al clasificar documento.',
    } as ClassifyResponse);
  }
});
